import { createAsyncThunk } from "@reduxjs/toolkit";
import { types, toast } from "@vilocnv/allsetra-core";
import { ObjectsService, UtilityService } from "app/data/services";
import {
  ICreateObjectRide,
  IRideBulkUpdateKeys,
  IRideBulkUpdateMode,
  IRideBulkUpdateDriver
} from "app/data/types";
import { objectAlarmConfigInitialData } from "./objectsSlice";
import {
  getObjectErrorMessage,
  getErrorMessage,
  getMileageCorrectionErrorMessage,
  getCreateObjectRideErrorMessage
} from "app/data/utils/errorUtils";
import {
  mapOperationalHoursError,
  normalizeOperationalHoursCorrection,
  OperationalHourCounter
} from "app/data/helpers/operationalHoursHelpers";
import i18n from "app/integrations/i18n";

export const getAllObjectsThunk = createAsyncThunk(
  "objects/getAllObjectsThunk",
  async (accountId: string) => {
    try {
      const response = await ObjectsService.getAllObjects(accountId);

      if (response.status === 200) {
        return response.data;
      }
    } catch (e: any) {
      console.error(e);
      throw new Error(e);
    }
  }
);

const REPORT_STATUS_CATALOG_PAGE_SIZE = 500;

type ReportStatusCatalogEntry = {
  uniqueId?: string;
  name?: string;
  aNumber?: string;
  serialNumber?: string;
  status?: string | number | null;
};

/** Pull device serials used by Activity/Stationary report joins. */
const extractObjectSerialNumbers = (object: any): string[] => {
  const serials = new Set<string>();
  if (typeof object?.serialNumber === "string" && object.serialNumber.trim()) {
    serials.add(object.serialNumber.trim());
  }
  if (Array.isArray(object?.devices)) {
    object.devices.forEach((device: any) => {
      if (
        typeof device?.serialNumber === "string" &&
        device.serialNumber.trim()
      ) {
        serials.add(device.serialNumber.trim());
      }
    });
  }
  return Array.from(serials);
};

/**
 * Slim catalog rows for report inactive join.
 * One entry per object; serialNumber is the first device serial when present.
 * Extra serials are duplicated as sibling entries so lookups can match any device.
 */
const mapObjectToStatusCatalogEntries = (
  object: any
): ReportStatusCatalogEntry[] => {
  if (!object) return [];

  const base: ReportStatusCatalogEntry = {
    uniqueId: object.uniqueId,
    name: object.name,
    aNumber: object.aNumber,
    status: object.status
  };
  const serials = extractObjectSerialNumbers(object);

  if (!serials.length) {
    return [base];
  }

  return serials.map((serialNumber) => ({
    ...base,
    serialNumber
  }));
};

/**
 * Status catalog for reports (includes DEACTIVATED).
 * Uses POST /objects/search with no Status where-clause (same as Objects list
 * with "show inactive" on). Does not touch map allObjects.
 */
export const getReportObjectStatusCatalogThunk = createAsyncThunk(
  "objects/getReportObjectStatusCatalogThunk",
  async (accountId: string) => {
    try {
      const catalog: ReportStatusCatalogEntry[] = [];
      let page = 1;
      let fetchedObjects = 0;
      let rowCount = Number.POSITIVE_INFINITY;

      while (fetchedObjects < rowCount) {
        const response = await ObjectsService.getObjectsByQuery(accountId, {
          page,
          itemsPerPage: REPORT_STATUS_CATALOG_PAGE_SIZE,
          where: [],
          orderBy: [{ field: "name", ascending: true }]
        } as types.IRecordsAggregationBody);

        if (response.status !== 200) {
          break;
        }

        const results = response.data?.results || [];
        rowCount = response.data?.rowCount ?? results.length;
        fetchedObjects += results.length;

        results.forEach((object: any) => {
          catalog.push(...mapObjectToStatusCatalogEntries(object));
        });

        if (
          !results.length ||
          results.length < REPORT_STATUS_CATALOG_PAGE_SIZE
        ) {
          break;
        }

        page += 1;
      }

      return catalog;
    } catch (e: any) {
      console.error(e);
      throw new Error(e);
    }
  },
  {
    condition: (accountId, { getState }) => {
      const objectsReducer = (getState() as any).rootReducer.objectsReducer;
      if (
        objectsReducer.reportObjectStatusCatalogLoaded &&
        objectsReducer.reportObjectStatusCatalogAccountId === accountId
      ) {
        return false;
      }
      if (
        objectsReducer.reportObjectStatusCatalogLoading &&
        objectsReducer.reportObjectStatusCatalogAccountId === accountId
      ) {
        return false;
      }
      return true;
    }
  }
);

export const getAllBasicInfoObjectsThunk = createAsyncThunk(
  "objects/getAllBasicInfoObjectsThunk",
  async (accountId: string) => {
    try {
      const response = await ObjectsService.getAllBasicInfoObjects(accountId);

      if (response.status === 200) {
        return response.data;
      }
    } catch (e: any) {
      console.error(e);
      throw new Error(e);
    }
  }
);

export const getInitialObjectsForDropdownListThunk = createAsyncThunk(
  "objects/getInitialObjectsForDropdownListThunk",
  async (accountId: string) => {
    try {
      const response =
        await ObjectsService.getInitialObjectsForDropdownList(accountId);

      if (response.status === 200) {
        return response.data;
      }
    } catch (e: any) {
      console.error(e);
      throw new Error(e);
    }
  }
);

export const exportObjectsCsvThunk = createAsyncThunk(
  "objects/exportObjectsCsvThunk",
  async ({
    accountId,
    params
  }: {
    accountId: string;
    params: Pick<types.IRecordsAggregationBody, "where" | "orderBy">;
  }) => {
    try {
      const response = await ObjectsService.exportObjects(accountId, params);
      return {
        data: response.data,
        headers: response.headers
      };
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  }
);

export const getObjectsByQueryThunk = createAsyncThunk(
  "objects/getObjectsByQueryThunk",
  async ({
    params,
    accountId,
    minimal
  }: {
    params: types.IRecordsAggregationBody;
    accountId: string;
    minimal?: boolean;
  }) => {
    try {
      const response = minimal
        ? await ObjectsService.getMinimalObjectsByQuery(accountId, params)
        : await ObjectsService.getObjectsByQuery(accountId, params);

      if (response.status === 200) {
        return response.data;
      }
    } catch (e: any) {
      console.error(e);
      throw new Error(e);
    }
  }
);

export const getSideMenuObjectsByQueryThunk = createAsyncThunk(
  "objects/getSideMenuObjectsByQueryThunk",
  async ({
    params,
    accountId,
    minimal
  }: {
    params: types.IRecordsAggregationBody;
    accountId: string;
    minimal?: boolean;
  }) => {
    try {
      const response = minimal
        ? await ObjectsService.getMinimalObjectsByQuery(accountId, params)
        : await ObjectsService.getObjectsByQuery(accountId, params);

      if (response.status === 200) {
        return response.data;
      }
    } catch (e: any) {
      console.error(e);
      throw new Error(e);
    }
  }
);

export const getObjectsLocationsThunk = createAsyncThunk(
  "objects/getObjectsLocationsThunk",
  async ({ accountId, values }: { accountId: string; values: any }) => {
    try {
      const response = await ObjectsService.getObjectsLocations(
        accountId,
        values
      );

      if (response.status === 200) {
        return response.data;
      } else {
        return [];
      }
    } catch (e: any) {
      console.error(e);
      throw new Error(e);
    }
  }
);

export const deleteObjectThunk = createAsyncThunk(
  "objects/deleteObjectThunk",
  async ({ accountId, objectId }: { accountId: string; objectId: string }) => {
    try {
      await ObjectsService.deleteObject(accountId, objectId);
      return objectId;
    } catch (e: unknown) {
      console.error(e);
      const friendlyErrorMessage = getErrorMessage(e);
      toast.error(friendlyErrorMessage);
      if (e instanceof Error) {
        throw e;
      }
      throw new Error(friendlyErrorMessage);
    }
  }
);

export const getSpecificObjectByIdThunk = createAsyncThunk(
  "objects/getSpecificObjectByIdThunk",
  async ({
    accountId,
    objectId,
    shouldFetchAlarmConfig = true
  }: {
    accountId: string;
    objectId: string;
    shouldFetchAlarmConfig?: boolean;
    silent?: boolean;
  }) => {
    try {
      const { data: specificObject } =
        await ObjectsService.getSpecificObjectById(accountId, objectId);

      let objectAlarmConfig = objectAlarmConfigInitialData;

      if (shouldFetchAlarmConfig) {
        const { data } = await ObjectsService.getObjectAlarmConfig(
          accountId,
          objectId
        );

        objectAlarmConfig = data;
      }

      return {
        specificObject,
        objectAlarmConfig
      };
    } catch (e: any) {
      console.error(e);
      const friendlyErrorMessage = getErrorMessage(e);
      toast.error(friendlyErrorMessage);
      throw new Error(e);
    }
  }
);

export const getObjectDataForMapThunk = createAsyncThunk(
  "objects/getObjectDataForMapThunk",
  async ({ accountId, objectId }: { accountId: string; objectId: string }) => {
    try {
      const { data } = await ObjectsService.getObjectDataForMap(
        accountId,
        objectId
      );

      return { ...data, uniqueId: objectId };
    } catch (e: any) {
      console.error(e);
      const friendlyErrorMessage = getErrorMessage(e);
      toast.error(friendlyErrorMessage, {
        toastId: `object-map-${objectId}`
      });
      throw new Error(e);
    }
  }
);

export const postUpdateObjectThunk = createAsyncThunk(
  "objects/postUpdateObjectThunk",
  async ({ accountId, data }: { accountId: string; data: any }) => {
    try {
      const response = await ObjectsService.postUpdateObject(accountId, data);

      return response;
    } catch (e: any) {
      console.error(e);
      const friendlyErrorMessage = getObjectErrorMessage(e, "update");
      toast.error(friendlyErrorMessage);
      throw new Error(e);
    }
  }
);

export const patchObjectMilageCorrectionThunk = createAsyncThunk(
  "objects/patchObjectMilageCorrectionThunk",
  async ({
    accountId,
    objectId,
    data
  }: {
    accountId: string;
    objectId: string;
    data: {
      newMileage: number;
      correctionDate: string;
    };
  }) => {
    try {
      const response = await ObjectsService.patchObjectMilageCorrection({
        accountId,
        objectId,
        data
      });

      return response;
    } catch (e: any) {
      console.error(e);
      const friendlyErrorMessage = getErrorMessage(e);
      toast.error(friendlyErrorMessage);
      throw new Error(e);
    }
  }
);

//
// Actions => Object Rides
//
export const getObjectRidesByQueryThunk = createAsyncThunk(
  "objects/getObjectRidesByQueryThunk",
  async ({
    params,
    accountId,
    objectId
  }: {
    params: types.IRecordsAggregationBody;
    accountId: string;
    objectId: string;
  }) => {
    try {
      const response = await ObjectsService.getObjectRidesByQuery(
        { accountId, objectId },
        params
      );

      if (response.status === 200) {
        return response.data;
      }

      return response;
    } catch (e: any) {
      console.error(e);
      throw new Error(e);
    }
  }
);

export const getObjectSpecificRideInfoThunk = createAsyncThunk(
  "objects/getObjectSpecificRideInfoThunk",
  async (data: { accountId: string; objectId: string; rideId: string }) => {
    try {
      const response = await ObjectsService.getObjectSpecificRideInfo(data);

      if (response.status === 200) {
        return response.data;
      }

      return response;
    } catch (e: any) {
      console.error(e);
      throw new Error(e);
    }
  }
);

export const getObjectRideLocationsThunk = createAsyncThunk(
  "objects/getObjectRideLocationsThunk",
  async (data: { accountId: string; objectId: string; rideId: string }) => {
    try {
      const response = await ObjectsService.getObjectRideLocations(data);

      if (response.status === 200) {
        return response.data;
      } else {
        return [];
      }
    } catch (e: any) {
      console.error(e);
      throw new Error(e);
    }
  }
);

export const updateObjectRideThunk = createAsyncThunk(
  "objects/updateObjectRideThunk",
  async ({
    accountId,
    objectId,
    rideId,
    data
  }: {
    accountId: string;
    objectId: string;
    rideId: string;
    data: any;
  }) => {
    try {
      const response = await ObjectsService.updateObjectRide(
        { accountId, objectId, rideId },
        data
      );

      return response;
    } catch (e: any) {
      console.error(e);
      const friendlyErrorMessage = getErrorMessage(e);
      toast.error(friendlyErrorMessage);
      throw new Error(e);
    }
  }
);

export const createObjectRideThunk = createAsyncThunk(
  "objects/createObjectRideThunk",
  async ({
    accountId,
    objectId,
    data
  }: {
    accountId: string;
    objectId: string;
    data: ICreateObjectRide;
  }) => {
    try {
      const response = await ObjectsService.createObjectRide(
        { accountId, objectId },
        data
      );

      if (response.status === 202) {
        const successKey = "addManualRideForm.rideBeingCreated";
        const translated = i18n.t(successKey);
        toast.success(
          translated !== successKey ? translated : "The ride is being created."
        );
      }

      return {
        status: response.status,
        headers: response.headers,
        data: response.data
      };
    } catch (e: any) {
      console.error(e);
      const friendlyErrorMessage = getCreateObjectRideErrorMessage(e);
      toast.error(friendlyErrorMessage);
      throw new Error(e);
    }
  }
);

export const updateRideBulkUploadKeysThunk = createAsyncThunk(
  "objects/updateRideBulkUploadKeysThunk",
  async ({
    accountId,
    data
  }: {
    accountId: string;
    data: IRideBulkUpdateKeys;
  }) => {
    try {
      const response = await ObjectsService.updateRideBulkUploadKeys(
        accountId,
        data
      );

      return response;
    } catch (e: any) {
      console.error(e);
      const friendlyErrorMessage = getErrorMessage(e);
      toast.error(friendlyErrorMessage);
      throw new Error(e);
    }
  }
);

export const updateRideBulkUploadModeThunk = createAsyncThunk(
  "objects/updateRideBulkUploadModeThunk",
  async ({
    accountId,
    data
  }: {
    accountId: string;
    data: IRideBulkUpdateMode;
  }) => {
    try {
      const response = await ObjectsService.updateRideBulkUploadMode(
        accountId,
        data
      );

      return response;
    } catch (e: any) {
      console.error(e);
      const friendlyErrorMessage = getErrorMessage(e);
      toast.error(friendlyErrorMessage);
      throw new Error(e);
    }
  }
);

export const updateRideBulkUploadDriverThunk = createAsyncThunk(
  "objects/updateRideBulkUploadDriverThunk",
  async ({
    accountId,
    data
  }: {
    accountId: string;
    data: IRideBulkUpdateDriver;
  }) => {
    try {
      const response = await ObjectsService.updateRideBulkUploadDriver(
        accountId,
        data
      );

      return response;
    } catch (e: any) {
      console.error(e);
      const friendlyErrorMessage = getErrorMessage(e);
      toast.error(friendlyErrorMessage);
      throw new Error(e);
    }
  }
);

export const updateAccountMileageThunk = createAsyncThunk(
  "objects/updateAccountMileageThunk",
  async ({
    accountId,
    objectId,
    data
  }: {
    accountId: string;
    objectId: string;
    data: any;
  }) => {
    try {
      const response = await ObjectsService.updateAccountMileage({
        accountId,
        objectId,
        data
      });

      if (response.status === 202) {
        toast.success("Mileage correction request has been sent");
      }
    } catch (e: any) {
      console.error(e);
      const friendlyErrorMessage = getMileageCorrectionErrorMessage(e);
      toast.error(friendlyErrorMessage);
      throw new Error(e);
    }
  }
);

export const patchObjectOperationalHoursThunk = createAsyncThunk(
  "objects/patchObjectOperationalHoursThunk",
  async (
    {
      accountId,
      objectId,
      data
    }: {
      accountId: string;
      objectId: string;
      data: {
        counter: OperationalHourCounter;
        newValue: number;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await ObjectsService.patchObjectOperationalHours({
        accountId,
        objectId,
        data
      });

      return normalizeOperationalHoursCorrection(response.data);
    } catch (e: unknown) {
      return rejectWithValue(mapOperationalHoursError(e));
    }
  }
);

export const getObjectOperationalHoursCorrectionsThunk = createAsyncThunk(
  "objects/getObjectOperationalHoursCorrectionsThunk",
  async (
    {
      accountId,
      objectId
    }: {
      accountId: string;
      objectId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response =
        await ObjectsService.getObjectOperationalHoursCorrections({
          accountId,
          objectId
        });
      const rows = Array.isArray(response.data) ? response.data : [];

      return rows
        .map(normalizeOperationalHoursCorrection)
        .filter((row): row is NonNullable<typeof row> => row !== null);
    } catch (e: unknown) {
      return rejectWithValue(mapOperationalHoursError(e));
    }
  }
);

// Object Features Actions
export const getObjectFeaturesThunk = createAsyncThunk(
  "objects/getObjectFeaturesThunk",
  async ({ accountId, objectId }: { accountId: string; objectId: string }) => {
    try {
      const response = await ObjectsService.getObjectFeatures(
        accountId,
        objectId
      );

      if (response.status === 200) {
        return response.data;
      } else {
        return [];
      }
    } catch (e: any) {
      console.log(e);
      console.error(e);
      throw new Error(e);
    }
  }
);

export const updateObjectAwayModeThunk = createAsyncThunk(
  "objects/updateObjectAwayModeThunk",
  async ({
    accountId,
    objectId,
    isEnabled
  }: {
    accountId: string;
    objectId: string;
    isEnabled: boolean;
  }) => {
    try {
      const response = await ObjectsService.updateObjectAwayMode(
        accountId,
        objectId,
        isEnabled
      );

      if (response.status === 200) {
        toast.success("Away mode updated successfully");
        return response.data;
      }
    } catch (e: any) {
      console.error(e);
      const friendlyErrorMessage = getObjectErrorMessage(e, "update");
      toast.error(friendlyErrorMessage);
      throw new Error(e);
    }
  }
);

export const objectsResolveAddressThunk = createAsyncThunk(
  "objects/objectsResolveAddressThunk",
  async ({ latitude, longitude }: { latitude: number; longitude: number }) => {
    try {
      const response = await UtilityService.getAddressFromLatLng(
        latitude,
        longitude
      );

      if (response.status === 200) {
        return response.data;
      }

      return response;
    } catch (e: any) {
      console.error(e);
      throw new Error(e);
    }
  }
);
